import { useMemo } from 'react';
import MixedBarLines from '../../../Components/Charts/Mixed/MixedBarLines';

export default function PerformanceChart({ chartDataSource }) {
    const dataPerf = useMemo(() => {
        const labels = chartDataSource.map(p =>
            new Date(p.workoutStart).toLocaleDateString('el-GR')
        );
        const currentDateTimestamp = Date.now();
        const pastWorkouts = chartDataSource.filter(
            workout => new Date(workout.workoutStart).getTime() <= currentDateTimestamp
        );
        const futureWorkouts = chartDataSource.slice(pastWorkouts.length - 1);
        const atlData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.atl
        }));

        const ctlData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.ctl
        }));

        const tsbData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.tsb
        }));

        const acwrData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.acwr
        }));

        const tssData = pastWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.tss
        }));

        const futureAtlData = futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.atl
        }));
        const futureCtlData = futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.ctl
        }));
        const futureTsbData = futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.tsb
        }));
        const futureAcwrData = futureWorkouts.map(workout => ({
            x: new Date(workout.workoutStart).toLocaleDateString('el-GR'),
            y: workout.acwr
        }));

        return {
            labels,
            datasets: [
                {
                    type: 'line',
                    label: 'Fatigue (ATL)',
                    borderColor: `rgb(234, 43, 35)`,
                    borderWidth: 2,
                    fill: false,
                    data: atlData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Future Fatigue (ATL)',
                    borderColor: `rgba(234, 43, 35, 0.5)`,
                    borderWidth: 2,
                    borderDash: [5, 5],
                    fill: false,
                    data: futureAtlData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Fitness (CTL)',
                    borderColor: `rgb(34, 168, 53)`,
                    borderWidth: 2,
                    fill: false,
                    data: ctlData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Future Fitness (CTL)',
                    borderColor: `rgba(34, 168, 53, 0.5)`,
                    borderWidth: 2,
                    fill: false,
                    data: futureCtlData,
                    borderDash: [5, 5],
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Form (TSB)',
                    borderColor: `rgb(88, 05, 251)`,
                    borderWidth: 2,
                    fill: false,
                    data: tsbData,
                    spanGaps: true,
                    yAxisID: 'y1'
                },
                {
                    type: 'line',
                    label: 'Future Form (TSB)',
                    borderColor: `rgba(88, 05, 251, 0.5)`,
                    borderWidth: 2,
                    fill: false,
                    data: futureTsbData,
                    spanGaps: true,
                    borderDash: [5, 5],
                    yAxisID: 'y1'
                },
                {
                    type: 'bar',
                    label: 'Stress (TSS)',
                    backgroundColor: `rgb(42, 85, 244)`,
                    borderColor: `rgb(42, 85, 244)`,
                    borderWidth: 2,
                    data: tssData,
                    maxBarThickness: 50,
                    yAxisID: 'y'
                },
            ],
            pastWorkouts,
            futureWorkouts,
            acwrData,
            futureAcwrData
        };
    }, [chartDataSource]);

    const chartOptions = useMemo(() => {
        const stressValues = [...dataPerf.datasets[6].data]
            .map(d => d.y)
            .filter(y => y !== undefined);
        const maxStress = Math.ceil(Math.max(...stressValues) * 1.1);
        const lineValues = [
            ...dataPerf.datasets[0].data,
            ...dataPerf.datasets[1].data,
            ...dataPerf.datasets[2].data,
            ...dataPerf.datasets[3].data,
            ...dataPerf.datasets[4].data,
            ...dataPerf.datasets[5].data
        ]
            .map(d => d.y)
            .filter(y => y !== undefined);

        const maxLine = Math.ceil(Math.max(...lineValues) * 1.1);
        const minLine = Math.floor(Math.min(...lineValues) * 1.1);

        // Scale the stress axis minimum by the ratio of maximums
        const stressMin = (maxStress / maxLine) * minLine;

        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: 'Performance',
                    font: {
                        size: window.innerWidth < 768 ? 14 : 16
                    }
                },
                legend: {
                    display: window.innerWidth >= 480,
                    position: window.innerWidth < 768 ? 'bottom' : 'top',
                    labels: {
                        usePointStyle: true,
                        padding: window.innerWidth < 768 ? 8 : 10,
                        font: {
                            size: window.innerWidth < 768 ? 10 : 12
                        },
                        generateLabels: chart => {
                            return chart.data.datasets
                                .map((dataset, i) => ({
                                    text: dataset.label,
                                    strokeStyle: dataset.borderColor || dataset.backgroundColor,
                                    fillStyle: dataset.borderColor || dataset.backgroundColor,
                                    pointStyle: 'line',
                                    hidden: !chart.isDatasetVisible(i),
                                    datasetIndex: i
                                }))
                                .filter(label => !label.text.includes('Future'));
                        }
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        maxRotation: window.innerWidth < 768 ? 45 : 0,
                        minRotation: window.innerWidth < 768 ? 45 : 0,
                        font: {
                            size: window.innerWidth < 768 ? 9 : 11
                        }
                    }
                },
                y: {
                    type: 'linear',
                    position: 'left',
                    title: {
                        display: window.innerWidth >= 480,
                        text: 'Stress',
                        color: 'rgb(148, 148, 148)',
                        font: {
                            size: window.innerWidth < 768 ? 10 : 12
                        }
                    },
                    ticks: {
                        font: {
                            size: window.innerWidth < 768 ? 9 : 11
                        }
                    },
                    min: stressMin,
                    max: maxStress,
                    grid: {
                        drawOnChartArea: false
                    }
                },
                y1: {
                    type: 'linear',
                    position: 'right',
                    title: {
                        display: window.innerWidth >= 480,
                        text: 'Fatigue / Fitness / Form',
                        color: 'rgb(148, 148, 148)',
                        font: {
                            size: window.innerWidth < 768 ? 10 : 12
                        }
                    },
                    ticks: {
                        font: {
                            size: window.innerWidth < 768 ? 9 : 11
                        }
                    },
                    min: minLine,
                    max: maxLine,
                    grid: {
                        drawOnChartArea: true
                    }
                }
            },
            animations: {
                tension: {
                    duration: 1500,
                    easing: 'easeInOutBounce',
                    from: 1,
                    to: 0
                }
            }
        };
    }, [dataPerf]);

    return (
        <div
            style={{
                width: '100%',
                height: window.innerWidth < 768 ? '400px' : '500px',
                maxHeight: window.innerWidth < 768 ? 'none' : '50vh',
                padding: window.innerWidth < 768 ? '10px' : '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: window.innerWidth < 768 ? '10px' : '20px',
                position: 'relative'
            }}
        >
            {window.innerWidth < 768 && (
                <div
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'rgba(51, 78, 172, 0.9)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '600',
                        zIndex: 10,
                        pointerEvents: 'none'
                    }}
                >
                    Pinch to zoom
                </div>
            )}
            <MixedBarLines data={dataPerf} options={chartOptions} />
        </div>
    );
}
